import React, { useState, useEffect, useCallback } from "react";
import SimpleSlider from "./SliderCustom";
// import SimpleSlider from "./SimpleSlider";
import styled, { css } from "styled-components";
import AudioPlay from "./AudioPlay";
import produce from "immer";
import { useAudios } from "../hooks/useAudios";
import { useImages } from "../hooks/useImages";
import { ReactComponent as Check } from "../assets/svg/check.svg";
import { ReactComponent as Wrong } from "../assets/svg/wrong.svg";
import md5 from "md5";

const LessonDataSelect = ({ seed, freeze = false, onChange = () => { }}) => {
  const [state, setState] = useState([]);

  const [extractedAudios, setExtractedAudios] = useState([]);
  const [extractedImages, setExtractedImages] = useState([]);

  const images = useImages(extractedImages);
  const audios = useAudios(extractedAudios);

  useEffect(() => {
    const state = initState(seed);
    setState(state);
    setExtractedImages(state.map((i) => i.image));
    setExtractedAudios(() => {
      const audios = [];
      state.forEach((item) => {
        audios.push(item.audio);
        item.alternatives.forEach((alternative) => {
          audios.push(alternative.audio);
        });
      });
      return audios;
    });
  }, [seed]);

  useEffect(() => {
    if (state && state.length !== 0) {
      onChange({
        ready: isReady(state),
        data: state,
      });
    }
  }, [state, onChange]);

  const onSelect = useCallback(
    (indexItem, indexAlternative) =>
      setState(
        produce((draftState) => {
          draftState[indexItem].alternatives.forEach((item, index, array) => {
            const indexCustom = getIndexFromAlternative(item);

            array[index].selected =
              indexCustom === indexAlternative ? !item.selected : false;
          });
        })
      ),
    []
  );

  const isLoaded = ({ image, audio }) =>
    ((image && images[image]) || !image) &&
    ((audio && audios[audio]) || !audio);

  return (
    <Container>
      <SimpleSlider
        length={state.length}
        onLastChild={() => { }}
        onChangeIndex={() => { }}
      >
        {state.map((item, index) => {
          const indexItem = index;
          const alternatives = item.alternatives;
          return (
            <BoxWrapper key={index}>
              {isLoaded(item) ? (
                <Box freeze={freeze}>
                  <BoxContent>
                    {item.image && <Image src={images[item.image]} />}
                    {(item.text || item.audio) && (
                      <Row>
                        {item.text && <Text>{item.text}</Text>}
                        {item.audio && (
                          <AudioPlayStyled audio={audios[item.audio]} />
                        )}
                      </Row>
                    )}
                  </BoxContent>
                  {alternatives.length > 0 && (
                    <AlternativesWrapper>
                      {alternatives.map((item, index) => {
                        const indexAlternative = getIndexFromAlternative(item);
                        return (
                          <Alternative
                            key={indexAlternative}
                            state={item.state}
                          >
                            {isLoaded(item) ? (
                              <>
                                {item.state && (
                                  <StateIcon as={stateIcon[item.state]} />
                                )}
                                <RadioWrapper
                                  selected={item.selected}
                                  onClick={() =>
                                    onSelect(indexItem, indexAlternative)
                                  }
                                >
                                  <Radio />
                                </RadioWrapper>
                                {item.text && <Text>{item.text}</Text>}
                                {item.audio && (
                                  <AudioPlayStyled audio={audios[item.audio]} />
                                )}
                              </>
                            ) : (
                              <Spinner src="loading.gif" />
                            )}
                          </Alternative>
                        );
                      })}
                    </AlternativesWrapper>
                  )}
                </Box>
              ) : (
                <Spinner src="loading-blue.gif" />
              )}
            </BoxWrapper>
          );
        })}
      </SimpleSlider>
    </Container>
  );
};

const stateIcon = {
  SUCCESS: Check,
  FAILURE: Wrong,
};

const initState = (seed) => {
  let state = formatState(seed);
  return state;
};

const formatState = (state) =>
  state.map((state) => ({ alternatives: [], ...state }));

const getIndexFromAlternative = (alternative) =>
  md5(alternative?.text + "-" + alternative?.audio);

const isReady = (state) => {
  let ready = true;
  for (let item of state) {
    let selected = false;
    for (let alternative of item.alternatives)
      if (alternative.selected) {
        selected = true;
        break;
      }
    if (!selected) {
      ready = false;
      break;
    }
  }
  return ready;
};

const Container = styled.div`
  width: 100%;
`;

const BoxWrapper = styled.div``; // NECESSARY

const Box = styled.div`
  display: flex;
  flex-direction: column;
  width: 95%;
  margin: 0 auto;
  ${(props) =>
    props.freeze &&
    css`
      pointer-events: none;
    `}
`;

const BoxContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 20px;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  max-height: 360px;
  object-fit: contain;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  padding: 10px 10px;
  & > * {
    margin: 5px;
  }
`;

const StateIcon = styled.div`
  width: 25px;
  height: 25px;
  min-width: 25px;
  min-height: 25px;
  position: absolute;
  bottom: 18px;
  left: 0px;
  right: 0px;
  margin: 0 auto !important;
`;

const AudioPlayStyled = styled(AudioPlay)``;

const Text = styled.div`
  font-size: 0.9em;
  color: black;

  & + ${AudioPlayStyled} {
    margin: 0 0 0 auto;
  }
`;

const AlternativesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 15px;
  background-color: #ffffff;
  border-radius: 20px;
  overflow: hidden;
`;

const Alternative = styled(Row)`
  &:not(:first-child):not(:last-child) {
    border-width: 1px 0px 1px 0px;
    border-style: solid;
    border-color: #eeeeee;
  }
  position: relative;
  ${(props) =>
    props.state &&
    css`
      padding-bottom: 60px;
    `}
`;

const Radio = styled.div`
  width: 15px;
  height: 15px;
  border-radius: 50%;
`;

const RadioWrapper = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  ${(props) =>
    props.selected
      ? css`
          ${Radio} {
            background-color: #caedff;
            border: 2px solid #b3e5fc;
          }
        `
      : css`
          ${Radio} {
            border: 2px solid #cccccc;
          }
        `}
`;

const Spinner = styled.img`
  margin: auto;
  width: 25px;
  height: 25px;
  display: block;
`;

export default LessonDataSelect;
